import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/ui';
import { FadeIn, PageTransition } from '@app/ui/motion';
import { formatCurrency } from '@app/validators';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/brands')({
  validateSearch: z.object({
    industry: z.string().optional(),
    search: z.string().optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.admin.brands.queryOptions({
        input: { industry: deps.industry, search: deps.search, page: deps.page },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: AdminBrands,
});

function AdminBrands() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const currentSearch = search.search || '';
  const [searchInput, setSearchInput] = useState(currentSearch);

  // Sync local input with URL changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const { data } = useSuspenseQuery(
    api.admin.brands.queryOptions({
      input: { industry: search.industry, search: search.search, page: search.page },
    }),
  );

  const { brands, total, page, totalPages } = data;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== currentSearch) {
        navigate({ search: (prev) => ({ ...prev, search: searchInput || undefined, page: 1 }) });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, currentSearch, navigate]);

  return (
    <PageTransition>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Brand Directory ({total})</h1>
        <div className="flex gap-2">
          <Select
            value={search.industry || 'all'}
            onValueChange={(v) =>
              navigate({ search: { ...search, industry: v === 'all' ? undefined : v, page: 1 } })
            }
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              <SelectItem value="apparel">Apparel</SelectItem>
              <SelectItem value="food_beverage">Food & Beverage</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
              <SelectItem value="fitness">Fitness</SelectItem>
              <SelectItem value="automotive">Automotive</SelectItem>
              <SelectItem value="financial">Financial Services</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="search"
            placeholder="Search brands..."
            className="w-48"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <FadeIn>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Industry</th>
                <th className="py-2 pr-4">Total Deals</th>
                <th className="py-2 pr-4">Avg Deal Size</th>
                <th className="py-2">Website</th>
              </tr>
            </thead>
            <tbody>
              {brands.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">
                    No brands found.
                  </td>
                </tr>
              ) : (
                brands.map((brand) => (
                  <tr key={brand.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        {brand.logo ? (
                          <img src={brand.logo} alt="" className="w-6 h-6 rounded-full" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                            {brand.name.charAt(0)}
                          </div>
                        )}
                        {brand.name}
                      </div>
                    </td>
                    <td className="py-2 pr-4">{brand.industry || '--'}</td>
                    <td className="py-2 pr-4">{brand.totalDeals}</td>
                    <td className="py-2 pr-4">
                      {brand.avgDealSize != null ? formatCurrency(Number(brand.avgDealSize)) : '--'}
                    </td>
                    <td className="py-2">
                      {brand.website ? (
                        <a
                          href={brand.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {brand.website.replace(/^https?:\/\//, '')}
                        </a>
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}
    </PageTransition>
  );
}
